import { type FC, useCallback, useEffect, useRef, useState } from 'react';
import { MilkdownProvider } from '@milkdown/react';
import clsx from 'clsx';

import type { BrainfishEditorProps } from '../types';
import { useHocusPocusCollaboration } from '../useHocusPocusCollaboration';
import { MilkdownEditor } from './EditorCore';

/**
 * Collab editor wrapper. While connecting, shows a read-only markdown preview; once
 * `collabState` exists, mounts collab underneath and removes the preview on `onReady`
 * (after Y.Doc is applied into ProseMirror). Preview and collab each get a
 * `MilkdownProvider` so unmounting preview does not tear down collab context.
 *
 * Collab content comes from Hocuspocus (`default` Y fragment), not `applyTemplate`.
 */
const BrainfishEditor: FC<BrainfishEditorProps> = ({ collabConfig, readOnly, ...editorProps }) => {
  // Don't create collab provider for readOnly editors - prevents cleanup race condition
  const { collabState, isCollabEnabled, replaceContentRef, refreshHeadingsRef } = useHocusPocusCollaboration(
    readOnly ? undefined : collabConfig,
  );

  const room = collabConfig?.room;
  const {
    className,
    onContentChange,
    reviewSuggestionsLoader,
    onReviewSuggestionAccept,
    onReviewSuggestionReject,
    onReviewSuggestionSourceClick,
    onReviewSuggestionResolve,
    onReviewSuggestionsResolved,
    reviewSuggestionBulkActionsRef,
    ...sharedEditorProps
  } = editorProps;

  const [showPreview, setShowPreview] = useState(isCollabEnabled);
  const previousRoomRef = useRef<string | undefined>();

  useEffect(() => {
    if (!isCollabEnabled) {
      previousRoomRef.current = undefined;

      return;
    }

    if (previousRoomRef.current !== undefined && previousRoomRef.current !== room) {
      setShowPreview(true);
    }

    previousRoomRef.current = room;
  }, [room, isCollabEnabled]);

  useEffect(() => {
    if (!isCollabEnabled) return;
    if (!collabState) {
      setShowPreview(true);
    }
  }, [collabState, isCollabEnabled]);

  const handleCollabReady = useCallback(() => {
    setShowPreview(false);
  }, []);

  const reviewSuggestionProps = {
    reviewSuggestionsLoader,
    onReviewSuggestionAccept,
    onReviewSuggestionReject,
    onReviewSuggestionSourceClick,
    onReviewSuggestionResolve,
    onReviewSuggestionsResolved,
    reviewSuggestionBulkActionsRef,
  };

  if (!isCollabEnabled) {
    return (
      <MilkdownEditor
        key="standalone"
        {...editorProps}
        readOnly={readOnly}
        replaceContentRef={replaceContentRef}
        refreshHeadingsRef={refreshHeadingsRef}
      />
    );
  }

  return (
    <div className={clsx('relative', className)} data-testid="brainfish-editor">
      {showPreview && (
        <div className={collabState ? 'absolute inset-0 z-10' : undefined}>
          <MilkdownProvider>
            <MilkdownEditor
              key={`preview-${room}`}
              {...sharedEditorProps}
              readOnly
              onContentChange={undefined}
            />
          </MilkdownProvider>
        </div>
      )}
      {collabState && (
        <MilkdownProvider>
          <MilkdownEditor
            key={`collab-${room}`}
            {...sharedEditorProps}
            {...reviewSuggestionProps}
            readOnly={readOnly}
            collabState={collabState}
            onContentChange={onContentChange}
            onReady={handleCollabReady}
            replaceContentRef={replaceContentRef}
            refreshHeadingsRef={refreshHeadingsRef}
          />
        </MilkdownProvider>
      )}
    </div>
  );
};

BrainfishEditor.displayName = 'BrainfishEditor';

export { BrainfishEditor };
